import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBuilding, 
  FaCalendarAlt, 
  FaChevronDown, 
  FaChevronUp,
  FaCheckCircle,
  FaTag
} from 'react-icons/fa';
import { format } from 'date-fns';

const ExperienceCard = ({ experience, index }) => {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (date) => {
    if (!date) return 'Present';
    return format(new Date(date), 'MMM yyyy');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="glass rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300"
    >
      {/* Header */}
      <div 
        className="p-6 cursor-pointer hover:bg-white/5 transition-colors duration-300"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400">
                <FaBuilding />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  {experience.role}
                </h3>
                <p className="text-gray-400">{experience.company}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1.5">
                <FaCalendarAlt className="text-xs" />
                {formatDate(experience.start_date)} - {experience.is_current ? 'Present' : formatDate(experience.end_date)}
              </span>
              {experience.is_current && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/20">
                  Current
                </span>
              )}
              {experience.category && (
                <span className="flex items-center gap-1.5">
                  <FaTag className="text-xs" />
                  {experience.category}
                </span>
              )}
            </div>
          </div>

          <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400">
            {expanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 border-t border-white/10">
              {/* Description */}
              <p className="text-gray-300 mb-4 leading-relaxed">
                {experience.description}
              </p>

              {/* Technologies */}
              {experience.technologies && experience.technologies.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Technologies</h4>
                  <div className="flex flex-wrap gap-2">
                    {experience.technologies.map((tech, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 text-xs rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Achievements */}
              {experience.achievements && experience.achievements.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Key Achievements</h4>
                  <ul className="space-y-2">
                    {experience.achievements.map((achievement, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-300">
                        <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ExperienceCard;